// External dependencies
import React from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import FaAngleLeft from 'react-icons/lib/fa/angle-left';
import { withTranslation } from 'react-i18next';

// Components
import MillerControl from '../../blocks/Miller';
import Dropdown from '../../../Global/Dropdown';
import { tooltipTypes, Tooltip } from '../../../Global/Tooltip';
import DomainPicker from "./DomainPicker"
import pendingImg from "../../../../images/pending.png"
import SelectSkillsCurriculum from '../ReportSettings/SelectSkillsCurriculum';
import ClassProgressReportType from './ClassProgressReportType';
import BadgingLocaleConfig from './BadgingLocaleConfig';

// Internal dependencies
import { getMetrics, comprehensiveAssessmentMetrics } from '../../values/metrics';
import rosterCreators from '../../state/roster/creators';
import teacherCreators from '../../state/teacher/creators';
import { msbType, locales, ASSESSMENT_TYPE_FILTERS } from '../../../../constants/constants';
import ScreeningWindowSelect from "Components/Reports/blocks/Select/ScreeningWindow";
import { TEACHER_LAYOUT } from '../../../Teacher/constants';
import UsageWindowSelect from '../../blocks/Select/UsageWindow';
import cloneDeep from 'lodash.clonedeep';
import TutorAssignmentButton from '../../../../features/tutor_assignment/components/TutorAssignmentButton';
import ViewAssignmentsModalButton from '../../../../features/tutor_assignment/components/ViewAssignmentsModalButton';
import { Toaster } from 'react-hot-toast';
const selectionTypesOpts = [
  {
    id: 'school',
    value: 'school',
    label: 'School',
  },
  {
    id: 'district',
    value: 'district',
    label: 'District',
  }
];

class TeacherSelections extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      entitySelected: selectionTypesOpts[1],//Default to district
    };

    this.onEntityChange = this.onEntityChange.bind(this);
    this.onSelectMetric = this.onSelectMetric.bind(this);
    this.onSelectBenchmark = this.onSelectBenchmark.bind(this);
    this.onSelectScale = this.onSelectScale.bind(this);
    this.handleLocaleChange = this.handleLocaleChange.bind(this);
    this.onMillerSelection = this.onMillerSelection.bind(this);
    this.handleToggleEquating = this.handleToggleEquating.bind(this);
    this.handleRosterDependentInitialization = this.handleRosterDependentInitialization.bind(this);
    this.handleReadingRiskChange = this.handleReadingRiskChange.bind(this);
    this.handleScreeningWindowChange = this.handleScreeningWindowChange.bind(this);
    this.handleStatePercentileCutsChange = this.handleStatePercentileCutsChange.bind(this);
    this.handleAssessmentTypeFilterChange = this.handleAssessmentTypeFilterChange.bind(this);
    this.getMetricsToReturn = this.getMetricsToReturn.bind(this);
    this.handleReportConfigChange = this.handleReportConfigChange.bind(this);
  }

  componentDidMount () {
    this.props.initializeRoster();
  }

  componentWillUnmount() {
    const isARM = this.props.selectedMetric && this.props.selectedMetric.value === "ARM";
    if (isARM) this.onSelectMetric(this.props.metrics[0]);
  }

  componentDidUpdate(prevProps, prevState) {
    // Once the roster is initialized, handle roster dependent intialization
    if(this.props.rosterStatus !== 'PENDING') {
      // If this is an ORF report, and metrics have been correctly populated, there should be 1 metric
      // Else, the num available metrics should match the metric options from values.
      let targetNumAvailableMetrics = getMetrics().length;
      let currMsbType = this.props.teacherSelectionTypes.msbType;
      if(currMsbType && currMsbType == msbType.ORF || currMsbType == msbType.DYSLEXIA) {
        targetNumAvailableMetrics = 1;
      } else if(currMsbType && currMsbType == msbType.COMPREHENSIVE) {
        targetNumAvailableMetrics += comprehensiveAssessmentMetrics.length;
      }
      if(this.props.metrics.length != targetNumAvailableMetrics && this.props.selectedClass && this.props.schools) {
        this.handleRosterDependentInitialization();
      } else if(this.props.district) {
        //If roster is complete, and the current class changed or it's the Custom Benchmark report, update selections accordingly
        if(prevProps.selectedClass != this.props.selectedClass
          || (this.props.teacherSelectionTypes.pageTitle === 'Custom Benchmark' && this.props.rosterStatus === 'COMPLETE' && !this.props.customBenchmarks)) {
          this.handleRosterDependentInitialization();
        } else if (prevProps.customBenchmarks && prevProps.customBenchmarks.status === 'PENDING' &&
          this.props.customBenchmarks && this.props.customBenchmarks.status === 'COMPLETE'){
          this.props.setDefaultSelections(this.props.district, this.props.students, this.props.teacherSelectionTypes.msbType);
        }
      }
    }
  }

  // Initializes values that change depending on the roster
  // This currently includes M/S/B and custom benchmarks
  handleRosterDependentInitialization() { // Note: function abstracted for readability
    this.props.setDefaultSelections(this.props.district, this.props.students, this.props.teacherSelectionTypes.msbType);
    this.props.loadCustomBenchmarks(this.props.district, this.props.selectedMetric); // TODO add school and state as params
  }

  handleToggleEquating(equatingSelected) {
    this.props.teacherSelect(
      {
        type: "equating",
        data: equatingSelected,
      },
      this.props.district,
      this.props.students
    );
  }

  onMillerSelection(data, currentSelectionType) {
    // Aggregate reports have class as primary selection
    this.props.rosterSelect({
      type: currentSelectionType,//type of selection
      data,
    });
  }

  onSelectMetric(selectedOption) {

    this.props.teacherSelect(
      {
        type: 'metric',
        data: selectedOption,
      },
      this.props.district,
      this.props.students
    );
  }

  onSelectScale(selectedOption) {
    this.props.teacherSelect(
      {
        type: 'scale',
        data: selectedOption,
      },
      this.props.district,
      this.props.students,
    );
  }

  onSelectBenchmark (selectedOption) {
    this.props.teacherSelect(
      {
        type: 'benchmark',
        data: selectedOption,
      },
      this.props.district,
      this.props.students,
    );
  }

  handleLocaleChange(selectedOption) {
    this.props.teacherSelect(
      {
        type: 'locale',
        data: selectedOption,
      },
      this.props.district,
      this.props.students,
    );
  }

  getMetricsToReturn(metrics) {
    return metrics;
  }

  handleReadingRiskChange(selectedOption){
    const { selectedReadingRisks } = this.props;

    this.props.teacherSelect(
      {
        type: 'readingRisks',
        data: selectedReadingRisks.includes(selectedOption)
          ? selectedReadingRisks.filter(t => t !== selectedOption) : [selectedOption, ...selectedReadingRisks],
      },
      this.props.district,
      this.props.students,
    );
  }

  handleScreeningWindowChange(selectedOption){
    this.props.teacherSelect(
      {
        type: 'screeningWindow',
        data: selectedOption,
      },
      this.props.district,
      this.props.students,
    );
  }

  handleStatePercentileCutsChange(selectedOption){
    const selectedStatePercentileCuts = this.props.statePercentileCuts.find(({state}) => selectedOption.value === state);
    this.props.teacherSelect(
      {
        type: 'statePercentileCuts',
        data: selectedStatePercentileCuts,
      },
      this.props.district,
      this.props.students,
    );
  }

  handleAssessmentTypeFilterChange(assessmentTypeSelected){
    this.props.teacherSelect(
      {
        type: 'assessmentTypeFilter',
        data: assessmentTypeSelected,
      },
      this.props.district,
      this.props.students,
    );
  }

  handleReportConfigChange(key, value) {
    const reportConfig = this.props.reportConfig;
    this.props.teacherSelect(
      {
        type: 'reportConfig',
        data: {...reportConfig, [key]: value},
      },
      this.props.district,
      this.props.students,
    )
  }

  handleChangeCurriculum = (selectedOption) => {
    this.props.teacherSelect(
      {
        type: 'curriculum',
        data: selectedOption,
      },
      this.props.district,
      this.props.students,
    );
  }

  onGoHome() {
    if (this.props.onBack) return () => this.props.onBack()
    if(this.props.isAdminReport) {
      return () => this.props.history.push('/admin');
    }
    return () => this.props.history.push('/teacher/reports');
  }

  onEntityChange(value) {
    this.setState({ entitySelected: value });
    this.props.onEntityChange(value);
  }

  render () {
    const { rosterStatus, type, status, batchPrint } = this.props;


    const noOptions = [{
      value: "none",
      label: "none"
    }];

    const isLoading = ( rosterStatus=== "PENDING" && status && status.type === "PENDING") || this.props.isReportLoading
    const isDisabled = batchPrint || isLoading || this.props.disabled;

    const isSkillsStatus = window.location.pathname.includes('skillsstatus');
    const isSkillDiagnostic = window.location.pathname.includes('skillsdiagnostic');


    // TODO: use helper components for scales, metrics, and benchmarks
    // Metric
    let disabledMetric = !(this.props.metrics && this.props.metrics.length > 1);

    // Scale
    let disabledScale = !(this.props.scales && this.props.scales.length > 1);

    // Percentile
    let disabledPercentile = this.props.statePercentileCuts && this.props.statePercentileCuts.length <= 1;

    const translateScaleLabel = (scale) => {
      if (!scale.value) return;
      scale.label = this.props.t(scale.value);
    };

    const scales = cloneDeep(this.props.scales);
    scales.forEach(translateScaleLabel);

    let scale =(
      <div className="reports-scale-selection">
        <div className="reportsAdditionalSelectionTitle"> {this.props.t("scale")}
          <Tooltip type={tooltipTypes.scale} />
        </div>
        <Dropdown
          options={scales}
          value={this.props.selectedScale}
          onChange={this.onSelectScale}
          isDisabled={disabledScale || isDisabled}
          isLoading={isLoading}
        />
      </div>
    );

    const isARM = this.props.selectedMetric && this.props.selectedMetric.value === "ARM";
    if (isARM) scale = null;

    // Benchmark
    // TODO abstract this logic somewhere. Reconsider approach.

    const translateBenchmarkLabel = (benchmark) => {
      if (!benchmark.label) return;
      const [type, grade] = benchmark.label.split(" - ");
      benchmark.label = [this.props.t(type), this.props.t(grade)].join(' - ');
    };

    let benchmarks = cloneDeep(this.props.benchmarks);
    benchmarks.forEach(translateBenchmarkLabel);

    let disabledBenchmark = !(benchmarks && benchmarks.length > 1);
    let benchmark;
    if(this.props.teacherSelectionTypes.msbType && this.props.teacherSelectionTypes.msbType == msbType.ORF) {
      benchmark = null;
    } else {
      benchmark = (
        <div className="reports-benchmark-selection">
          <div className="reportsAdditionalSelectionTitle"> {this.props.t("benchmarks")}
            <Tooltip type={tooltipTypes.benchmarks} />
          </div>
          <Dropdown
            options={benchmarks}
            value={this.props.selectedBenchmark}
            onChange={this.onSelectBenchmark}
            isDisabled={disabledBenchmark || isDisabled}
            isLoading={isLoading}
          />
        </div>
      );
    }

    let enableScoreScaling = this.props.teacherSelectionTypes.hasScoreScaling;
    if(!this.props.teacherSelectionTypes.isAggregateReport && !this.props.selectedStudent) {
      if(!this.props.teacherSelectionTypes.msbType || this.props.teacherSelectionTypes.msbType != msbType.ORF) {
        enableScoreScaling = false;
      }
    }
    const additionalSelectStyle = cx({
      "reportsAdditionalSelections": true,
      "hidden": !enableScoreScaling,
    });

    let backText = this.props.t("back_to_reports");
    if(this.props.isAdminReport) {
      backText = "BACK TO ADMIN";
    }
    if (this.props.backText) backText = this.props.backText

    const radioClass = cx({
      "reportsRadioButton": true,
      "is-disabled": isDisabled,
      "is-loading": isLoading
    })

    let metricsOptions = cloneDeep(this.props.metrics);

    if(this.props.teacherSelectionTypes.pageTitle === "Parent Report") {
        if (this.props.selectedLocale === locales.ES_MX) {
          metricsOptions = metricsOptions.filter(item => ['ARM', 'ORF'].includes(item.value));
        } else {
          // Include all items in the original array if the locale is not 'es-mx'
          metricsOptions = metricsOptions.filter(item => ['ARM', 'ORF'].includes(item.value));
        }
    }

    if(this.props.teacherSelectionTypes.pageTitle === "Custom Benchmark") {
        metricsOptions = metricsOptions.filter(item => ['ARM', 'ORF'].includes(item.value));
        benchmark = null;
    }

    const translateMetricLabel = (metric) => {
      if (!metric.value) return;
      metric.label = this.props.t(metric.value)
    };

    metricsOptions.forEach(translateMetricLabel);

    let isPending = false;

    if (get(this.props, "readingRopeReport.data.isPending") && this.props.selectedStudent) {
        isPending = true;
    }

    const selectionClasses = cx({
      "report-selections": true,
      [this.props.layout || TEACHER_LAYOUT.sidebar]: true
    })

    const metricsToReturn = this.getMetricsToReturn(this.props.metrics);

    return (
      <div className={selectionClasses} style={this.props.containerStyle}>
        {this.props.active &&
          <div className="report-selection-body">
            <div className="report-selection-body-main">
              <div className='__top-section'>
                <div
                  className="teacherSelectionBack"
                  onClick={this.onGoHome()}>
                  <FaAngleLeft size="22" className="teacherBackBtn"/>
                  {backText}
                </div>
                <div className="teacherSelectionHeader"> {this.props.t(this.props.teacherSelectionTypes.pageTitle)}</div>
                {this.props.enableSchoolDistrictToggle && (
                  <div className='reportsSubselection' style={{ maxWidth: '260px' }}>
                    <div className="reportsMetricConfigTitle" style={{ padding: '12px 0px 4px 0px' }}> TYPE </div>
                    <Dropdown
                      options={selectionTypesOpts}
                      value={this.state.entitySelected}
                      onChange={this.onEntityChange}
                      isDisabled={false}
                    />
                  </div>
                )}
                {!this.props.teacherSelectionTypes.disabledRostering && (this.state.entitySelected === selectionTypesOpts[0] || !this.props.enableSchoolDistrictToggle) && (
                <React.Fragment>
                  {this.props.enableSchoolDistrictToggle && <div className="reportsMetricConfigTitle" style={{padding: '12px 0 4px 0'}}> SELECT SCHOOL </div>}
                  <MillerControl
                    type={type}
                    types={this.props.types}
                    search={get(this.props, `search.${type}`)}
                    recent={get(this.props, `recent.${type}`)}
                    results={get(this.props, `results.${type}`)}
                    onType={this.onType}
                    onSelect={this.onSelect}
                    onSearch={this.onSearch}
                    isAggregateReport={this.props.teacherSelectionTypes.isAggregateReport}
                    isSchoolReport={this.props.teacherSelectionTypes.isSchoolReport}
                    schools={this.props.schools.map(school => school.ui)}
                    classes={this.props.classrooms.map(classroom => classroom.ui)}
                    students={this.props.students.map(student => student.ui)}
                    selectedSchool={this.props.selectedSchool}
                    selectedClass={this.props.selectedClass}
                    selectedStudent={this.props.selectedStudent}
                    onMillerSelection={this.onMillerSelection}
                    rosterStatus={this.props.rosterStatus}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                  />
                </React.Fragment>
                )}
                {this.props.teacherSelectionTypes.hasScoreScaling && <div className="reportsMetricConfigTitle"> {this.props.t("score_configuration")} </div>}
              </div>
              <div className='assignment-buttons-container'>
                <div style={{
                display: 'flex',
                alignItems: 'center',
                }}>
                  {this.props.teacherSelectionTypes.hasTutorAssignmentButton && <TutorAssignmentButton />}
                  {this.props.teacherSelectionTypes.hasViewAssignmentsModalButton && <ViewAssignmentsModalButton />}
                </div>
              </div>
              <div className='__configuration-section'>
              {!this.props.isAdminReport && (isSkillsStatus || isSkillDiagnostic) &&
                <SelectSkillsCurriculum
                  handleChangeCurriculum={this.handleChangeCurriculum}
                  isLoading={isLoading}
                  isDisabled={isDisabled}
                />}
              {isSkillsStatus && <DomainPicker />}
              {this.props.teacherSelectionTypes.hasClassProgressReportType &&
                <ClassProgressReportType
                  isDisabled={isDisabled}
                  isLoading={isLoading}
                />
              }
              {this.props.teacherSelectionTypes.hasScoreScaling &&
                <div className={additionalSelectStyle}>
                  <div>
                    <div className="reportsAdditionalSelectionTitle"> {this.props.t("metric")}
                      <Tooltip type={tooltipTypes.metric} />
                    </div>
                    <Dropdown
                      options={metricsOptions}
                      value={this.props.selectedMetric}
                      onChange={this.onSelectMetric}
                      isDisabled={disabledMetric || isDisabled}
                      isLoading={isLoading}
                    />
                  </div>
                  {this.props.selectedMetric && !this.props.isAdminReport && ["ORF", "ARM"].includes(this.props.selectedMetric.value) && !this.props.hideScore &&
                  <div className="reportsSubselection">
                    <div className="reportsAdditionalSelectionTitle"> {this.props.t("score_type")}
                      <Tooltip type={tooltipTypes.scoreType} />
                    </div>
                    <div className="reportsRadioButtonSet">
                      <div className={radioClass} >
                        <input
                          type="radio"
                          onChange={() => this.handleToggleEquating(true)}
                          id="equated_radio_btn"
                          checked={this.props.selectedEquating}
                          name="selector"
                          disabled={isDisabled} />
                        <label htmlFor="equated_radio_btn">{this.props.t("equated")}</label>
                        <div className="check"></div>
                      </div>
                      <div className={radioClass} >
                        <input
                          type="radio"
                          id="unequated_radio_btn"
                          onChange={() => this.handleToggleEquating(false)}
                          checked={!this.props.selectedEquating}
                          name="selector"
                          disabled={isDisabled} />
                        <label htmlFor="unequated_radio_btn">{this.props.t("unequated")}</label>
                        <div className="check"></div>
                      </div>
                    </div>
                  </div>
                  }
                  {!this.props.hideScore && scale}
                  {benchmark}
                </div>}
                {this.props.teacherSelectionTypes.hasLanguageSelection &&
                <div className="reportsSubselection">
                  <div className="reportsAdditionalSelectionTitle"> {this.props.t("language")} </div>
                  <div className="reportsRadioButtonSet">
                    <div className={radioClass} >
                      <input
                        type="radio"
                        id="en-us_radio_btn"
                        onChange={() => this.handleLocaleChange(locales.EN_US)}
                        checked={this.props.selectedLocale === locales.EN_US}
                        name="languageOption"
                        disabled={isDisabled} />
                      <label htmlFor="en-us_radio_btn">{this.props.t("english")}</label>
                      <div className="check"></div>
                    </div>
                    <div className={radioClass} >
                      <input
                        type="radio"
                        id="es-mx_radio_btn"
                        onChange={() => this.handleLocaleChange(locales.ES_MX)}
                        checked={this.props.selectedLocale === locales.ES_MX}
                        name="languageOption"
                        disabled={isDisabled} />
                      <label htmlFor="es-mx_radio_btn">{this.props.t("spanish")}</label>
                      <div className="check"></div>
                    </div>
                  </div>
                </div>}
                {this.props.teacherSelectionTypes.hasReadingRiskType &&
                <div className="reportsSubselection">
                  <div className="reportsAdditionalSelectionTitle">{this.props.t("reading_risk_type")}</div>
                  <div className="reportsCheckbox">
                    <label>
                      <input
                        id={locales.EN_US}
                        type="checkbox"
                        checked={this.props.selectedReadingRisks.includes(locales.EN_US)}
                        onChange={() => this.handleReadingRiskChange(locales.EN_US)}
                        disabled={isDisabled}
                      />
                      {this.props.t("english_reading_risk")}
                    </label>
                  </div>
                  <div className="reportsCheckbox">
                    <label>
                      <input
                        id={locales.ES_MX}
                        type="checkbox"
                        checked={this.props.selectedReadingRisks.includes(locales.ES_MX)}
                        onChange={() => this.handleReadingRiskChange(locales.ES_MX)}
                        disabled={isDisabled}
                      />
                      {this.props.t("spanish_reading_risk")}
                    </label>
                  </div>
                </div>}
                {this.props.teacherSelectionTypes.hasScreeningWindowSelection &&
                <div className="reportsSubselection">
                  <div className="reportsAdditionalSelectionTitle">{this.props.t("testing_window")}</div>
                  <ScreeningWindowSelect
                    selection={this.props.selectedScreeningWindow}
                    onChange={this.handleScreeningWindowChange}
                    disabled={isDisabled}
                    isLoading={isLoading}/>
                </div>
                }
                {this.props.teacherSelectionTypes.hasStatePercentileCutsSelection && this.props.statePercentileCuts &&
                <div className="reportsSubselection">
                  <div className="reportsAdditionalSelectionTitle">STUDENT CLASSIFICATION</div>
                  <div>
                    <Dropdown
                      options={this.props.statePercentileCuts.map(({state,label}) => { return {value: state, label: label }})}
                      value={{value: this.props.selectedStatePercentileCuts.state, label: this.props.selectedStatePercentileCuts.label}}
                      onChange={this.handleStatePercentileCutsChange}
                      isDisabled={disabledMetric || disabledPercentile || isDisabled}
                      isLoading={isLoading}
                    />
                  </div>
                </div>
              }
              {this.props.teacherSelectionTypes.hasAssessmentTypeFilter &&
                <div className="reportsSubselection">
                  <div className="reportsAdditionalSelectionTitle"> {this.props.t("assessment_type")} </div>
                  <div className="reportsRadioButtonSet">
                    <div className={radioClass} >
                      <input
                        type="radio"
                        id="benchmark-only_radio_btn"
                        onChange={() => this.handleAssessmentTypeFilterChange(ASSESSMENT_TYPE_FILTERS.BENCHMARK_ONLY)}
                        checked={this.props.selectedAssessmentTypeFilter === ASSESSMENT_TYPE_FILTERS.BENCHMARK_ONLY}
                        name="filterOption"
                        disabled={isDisabled} />
                      <label htmlFor="benchmark-only_radio_btn">{this.props.t("benchmark_only")}</label>
                      <div className="check"></div>
                    </div>
                    <div className={radioClass} >
                      <input
                        type="radio"
                        id="latest-assessment_radio_btn"
                        onChange={() => this.handleAssessmentTypeFilterChange(ASSESSMENT_TYPE_FILTERS.MOST_RECENT_ASSESSMENT)}
                        checked={this.props.selectedAssessmentTypeFilter === ASSESSMENT_TYPE_FILTERS.MOST_RECENT_ASSESSMENT}
                        name="filterOption"
                        disabled={isDisabled} />
                      <label htmlFor="latest-assessment_radio_btn">{this.props.t("latest_assessment")}</label>
                      <div className="check"></div>
                    </div>
                  </div>
                </div>
              }
              {this.props.teacherSelectionTypes.hasSubscoresConfig &&
                <div className="reportsSubselection">
                  <div className="reportsAdditionalSelectionTitle">{this.props.t("configuration")}</div>
                  <div className="reportsRadioButtonSet">
                    <div className="reportsCheckbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={this.props.reportConfig?.isSubscoresVisible || false}
                          onChange={() => this.handleReportConfigChange('isSubscoresVisible', !this.props.reportConfig.isSubscoresVisible)}
                          disabled={isDisabled}
                        />
                        {this.props.t("show_subscores")}
                      </label>
                    </div>
                    <div className="reportsCheckbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={this.props.reportConfig?.isTipsVisible || false}
                          onChange={() => this.handleReportConfigChange('isTipsVisible', !this.props.reportConfig.isTipsVisible)}
                          disabled={isDisabled}
                        />
                        {this.props.t("show_tips")}
                      </label>
                    </div>
                  </div>
                </div>
              }
              {this.props.teacherSelectionTypes.hasUsageWindowSelection &&
                <div className="reportsSubselection">
                  <div className="reportsAdditionalSelectionTitle">{this.props.t("usage_window")}</div>
                  <UsageWindowSelect isDisabled={isDisabled} />
                </div>
              }
              {this.props.teacherSelectionTypes.hasBadgingLocaleConfig &&
                <BadgingLocaleConfig isDisabled={isDisabled} />
              }
              {isPending && window.location.pathname.includes("readingrope") && <img src={pendingImg} className="pending-img"/>}
            </div>
            </div>
          </div>
        }
      </div>
    )
  }
}

// TODO: refine this redux
const mapStateToProps = (state) => {
  const { roster } = state.reports;

  // report is loading if any of the reports has status of pending
  const reportKeys = Object.keys(state.reports)
  const isLoading = reportKeys.some(( key ) => get(state, `reports.${key}.report.status.type`) === 'PENDING')

  return {
    metrics: get(state, 'reports.teacher.metrics', []),
    scales: get(state, 'reports.teacher.scales', []),
    benchmarks: get(state, 'reports.teacher.benchmarks', []),
    roster: get(state, 'reports.roster.data'),
    status: get(state, 'reports.roster.status'),
    customBenchmarks: get(state, 'reports.teacher.customBenchmarks'),
    applicableGrades: get(state, 'reports.teacher.applicableGrades'),
    district: roster.data && roster.data.district ? roster.data.district[0].ui : null,
    selectedSchool: roster.ui && roster.ui.selections.school ? roster.ui.selections.school : null,
    selectedClass: roster.ui && roster.ui.selections.classroom ? roster.ui.selections.classroom : null,
    selectedStudent: roster.ui && roster.ui.selections.student ? roster.ui.selections.student : null,
    selectedCurriculum: get(state, 'reports.teacher.selectedCurriculum', null),
    students: roster.data && roster.data.student ? roster.data.student : [],
    classrooms: roster.data && roster.data.classroom ? roster.data.classroom : [],
    schools: roster.data && roster.data.school ? roster.data.school : [],
    rosterStatus: roster.status ? roster.status.type : null,
    active: get(state, 'reports.roster.ui.active', true),
    type: get(state, 'reports.roster.ui.type'),
    types: get(state, 'reports.roster.ui.types'),
    selectedAssessmentTypeFilter: get(state, 'reports.teacher.selectedAssessmentTypeFilter'),
    selectedLocale: get(state, 'reports.teacher.selectedLocale', null),
    selectedReadingRisks: get(state, 'reports.teacher.selectedReadingRisks', []),
    selectedScreeningWindow: get(state, 'reports.teacher.selectedScreeningWindow', null),
    statePercentileCuts: get(state, 'reports.teacher.statePercentileCuts', null),
    selectedStatePercentileCuts: get(state, 'reports.teacher.selectedStatePercentileCuts', null),
    isReportLoading: isLoading,
    selectedDomain: get(state, 'reports.domain'),
    batchPrint: roster.batchPrint,
    readingRopeReport: get(state, 'reports.readingrope.report', null),
    reportConfig: get(state, 'reports.teacher.reportConfig', null),
  }
};

const mapDispatchToProps = (dispatch) => ({
  rosterSelect: (selection) => dispatch(rosterCreators.rosterSelect(selection)),
  initializeRoster: () => dispatch(rosterCreators.initializeRoster()),
  teacherSelect: (selection, district, students) => dispatch(teacherCreators.teacherSelect(selection, district, students)),
  setDefaultSelections: (district, students, msbType) => dispatch(teacherCreators.teacherDefaultSelections(district, students, msbType)),
  loadCustomBenchmarks: (district, metric) => dispatch(teacherCreators.loadCustomBenchmarks(district, metric)),
});

// TODO update prop types
TeacherSelections.propTypes = {
  layout: PropTypes.string,
  active: PropTypes.bool,
  recent: PropTypes.object,
  search: PropTypes.string,
  teacherSelectionTypes: PropTypes.object,
  status: PropTypes.object, // container-level status
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation('reports_sidebar')(TeacherSelections));
